import { COMPARISON_KEY } from 'lib/consts';
import { createContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface SelectedProduct {
  productId: string | number;
  sku: string;
}

interface ComparisonContext {
  selected: SelectedProduct[] | null;
  addFrame: (frame: SelectedProduct) => void;
  removeFrame: (frame: SelectedProduct) => void;
  updateSelection: (frame: SelectedProduct) => void;
}

export const ComparisonContext = createContext<ComparisonContext>({
  selected: null,
  addFrame: () => {
    return;
  },
  removeFrame: () => {
    return;
  },
  updateSelection: () => {
    return;
  },
});

export const ComparisonProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [selected, setSelected] = useState<SelectedProduct[] | null>(null);

  const addFrame = (frame: SelectedProduct): void => {
    selected ? setSelected((old) => [...(old as SelectedProduct[]), frame]) : setSelected([frame]);
  };

  const removeFrame = (frame: SelectedProduct): void => {
    const updated = selected?.filter((item) => item.sku !== frame.sku);
    setSelected(updated && updated.length > 0 ? [...updated] : null);
  };

  const updateSelection = (frame: SelectedProduct): void => {
    const updated = selected?.map((item) => {
      if (item.productId === frame.productId) item.sku = frame.sku;

      return item;
    });

    setSelected([...(updated as SelectedProduct[])]);
  };

  const updateStorage = useCallback(() => {
    try {
      if (selected && selected.length > 0)
        sessionStorage.setItem(COMPARISON_KEY, JSON.stringify(selected));

      if (!selected) sessionStorage.removeItem(COMPARISON_KEY);
    } catch (error) {
      console.log('sessionStorage not supported');
    }
  }, [selected]);

  useEffect(() => {
    try {
      const items = sessionStorage.getItem(COMPARISON_KEY);
      const options = items ? JSON.parse(items) : null;
      setSelected(options);
    } catch (error) {
      setSelected(null);
    }
  }, []);

  useEffect(() => {
    updateStorage();
  }, [selected, updateStorage]);

  return (
    <ComparisonContext.Provider value={{ selected, addFrame, removeFrame, updateSelection }}>
      {children}
    </ComparisonContext.Provider>
  );
};
